import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@/core/api";
import type { ICreateAiScriptRunResponse } from "@/payroll/ai-script/types";
import {
  type CreatedAiScriptRunByPeriodStart,
  applyCreatedAiScriptRunToPayPeriods,
  createdAiScriptRunsByPeriodKey,
} from "@/payroll/ai-script/utils";
import { payPeriodsKey } from "@/payroll/pay-periods/query";
import type { EnrichedPayPeriod } from "@/payroll/payroll.d";

interface CreateAiScriptRunParams {
  startDate: string;
  endDate: string;
  aiScriptVersionId?: string | null;
  payrollRosterId?: string | null;
  sheetNames?: string[] | null;
}

export const useCreateAiScriptRun = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      startDate,
      endDate,
      aiScriptVersionId,
      payrollRosterId,
      sheetNames,
    }: CreateAiScriptRunParams) => {
      if (!businessId) throw new Error("Business is required");

      return api
        .post(`/businesses/${businessId}/ai_script_runs`, {
          start_date: startDate,
          end_date: endDate,
          ...(aiScriptVersionId
            ? { ai_script_version_id: aiScriptVersionId }
            : {}),
          ...(payrollRosterId ? { payroll_roster_id: payrollRosterId } : {}),
          ...(sheetNames?.length ? { sheet_names: sheetNames } : {}),
        })
        .then((res) => res.data as ICreateAiScriptRunResponse);
    },
    onSuccess: (response, { startDate, endDate }) => {
      const createdRun = { startDate, endDate, run: response.run };

      queryClient.setQueryData<CreatedAiScriptRunByPeriodStart>(
        createdAiScriptRunsByPeriodKey(businessId),
        (runs) => ({
          ...runs,
          [startDate]: createdRun,
        }),
      );
      queryClient.setQueryData<EnrichedPayPeriod[]>(
        payPeriodsKey(businessId),
        (periods) => applyCreatedAiScriptRunToPayPeriods(periods, createdRun),
      );
      queryClient.invalidateQueries({ queryKey: payPeriodsKey(businessId) });
    },
  });
};
